import { useQuery } from '@tanstack/react-query';
import { apiGetFresh } from '../lib/api';
import { getApiBase } from '../lib/submissions';

interface UseEntityVersionOptions {
  /** Path relative to the API base, e.g. '/campaign-version'. */
  endpoint: string;
  /** Base segment of the version probe's React Query key (wrapped in an array below). */
  queryKeyBase: string;
  pollMs: number;
}

async function fetchVersion(url: string): Promise<string> {
  const res = await apiGetFresh(url);
  if (!res.ok) {
    throw new Error(`Failed to load version (${res.status})`);
  }
  const data = await res.json();
  return data.version;
}

/**
 * Version changes force a cache-busting fetch; unchanged values keep hitting
 * the existing browser/CDN cache. See useCampaignVersion / useFeaturedVideoPage.
 */
export default function useEntityVersion({ endpoint, queryKeyBase, pollMs }: UseEntityVersionOptions) {
  const url = `${getApiBase().replace(/\/+$/, '')}${endpoint}`;

  const query = useQuery<string, Error>({
    queryKey: [queryKeyBase],
    queryFn: () => fetchVersion(url),
    refetchInterval: pollMs,
    refetchOnWindowFocus: false,
  });

  return {
    version: query.data,
    error: query.error,
    refetch: query.refetch,
  };
}
